import { Dispatch } from 'react';

import {
	DATAINGESTIONTABS,
	FILE_ALREADY_EXISTS_ERROR_MESSAGE,
	REGULATIONS,
} from 'common/constants/DataIngestionConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { PDF_LABEL, HTML_LABEL } from 'common/constants/index';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import {
	setSelectedDataIngestionTab,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setFileMappingData,
	setParseFileData,
} from 'common/store/reducer/data-ingestion-reducer';

import { UploadResponseOptions } from './api/type-def';

const { ALL, PROCESSING, VALIDATING, FAILED, COMPLETED } = DATAINGESTIONTABS;
const { BAD_REQUEST } = HTTP_STATUS_CODES;

export const updateTabName = (index: number, updateTab: Dispatch<any>) => {
	let tab = {
		dataIngestionTabIndex: -1,
		dataIngestionTabName: '',
	};
	switch (index) {
		case 0:
			tab = {
				dataIngestionTabIndex: index,
				dataIngestionTabName: ALL,
			};
			break;
		case 1:
			tab = {
				dataIngestionTabIndex: index,
				dataIngestionTabName: VALIDATING,
			};
			break;
		case 2:
			tab = {
				dataIngestionTabIndex: index,
				dataIngestionTabName: PROCESSING,
			};
			break;
		case 3:
			tab = {
				dataIngestionTabIndex: index,
				dataIngestionTabName: FAILED,
			};
			break;
		case 4:
			tab = {
				dataIngestionTabIndex: index,
				dataIngestionTabName: COMPLETED,
			};
			break;
		default:
			break;
	}

	updateTab(setSelectedDataIngestionTab(tab));
};

export const handleUploadResponse = ({
	uploadResponse,
	uploadError,
	dispatch,
	setShowStartProcessingButton,
	setLoading,
	setFileUploadClick,
	setStartProcessing,
	onUploadSuccess,
}: UploadResponseOptions) => {
	if (uploadResponse) {
		dispatch(setFileMappingData(uploadResponse.data));
		setShowStartProcessingButton(true);
		setLoading(false);
		setFileUploadClick(false);
		setStartProcessing(false);
		if (onUploadSuccess) {
			onUploadSuccess();
		}
	} else if (uploadError) {
		if (uploadError?.status === BAD_REQUEST) {
			const errorMsg = uploadError?.errorMessage as string;
			dispatch(
				setToastMessage({
					message: errorMsg.includes('Blob already exists with the name')
						? FILE_ALREADY_EXISTS_ERROR_MESSAGE
						: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
				}),
			);
		}
	}
};
export const checkFileType = (
	dataType: string,
	selectedUploadFileType: string,
	fileData: File[],
	dispatch: Dispatch<any>,
) => {
	if (
		dataType === REGULATIONS &&
		(selectedUploadFileType === PDF_LABEL ||
			selectedUploadFileType === HTML_LABEL)
	) {
		const path = URL.createObjectURL(fileData[0]);
		dispatch(setParseFileData(path));
	}
};

interface Element {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

const calculateDistance = (
	x1: number,
	y1: number,
	x2: number,
	y2: number,
): number => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

// Function to find the immediate previous and immediate next elements based on distance
export const findImmediatePreviousAndNext = (
	quadrants: Element[],
	newElement: Element,
) => {
	// Calculate the center of the new element
	const newCenterX = (newElement.x1 + newElement.x2) / 2;
	const newCenterY = (newElement.y1 + newElement.y2) / 2;

	// Create an array of distances from the new element to the center of each quadrant
	const distances = quadrants.map((quadrant) => {
		// Calculate center of the quadrant
		const centerX = (quadrant.x1 + quadrant.x2) / 2;
		const centerY = (quadrant.y1 + quadrant.y2) / 2;

		// Calculate distance from the center of the new element to the center of the quadrant
		const distance = calculateDistance(
			newCenterX,
			newCenterY,
			centerX,
			centerY,
		);

		return { quadrant, distance, centerX, centerY };
	});

	// Sort the array by distance (ascending)
	distances.sort((a, b) => a.distance - b.distance);

	// Find the immediate previous and next elements based on the sorted distances
	let immediatePrevious = null;
	let immediateNext = null;

	for (let i = 0; i < distances.length; i += 1) {
		const current = distances[i];

		// If this is the first element, then it's the nearest next
		if (i === 0) {
			immediateNext = current;
		}

		// If this is the last element, then it's the nearest previous
		if (i === distances.length - 1) {
			immediatePrevious = current;
		}

		// Now, break out once the nearest previous and next are found
		if (i > 0 && i < distances.length - 1) {
			immediatePrevious = distances[i - 1];
			immediateNext = distances[i + 1];
		}
	}

	return {
		immediatePrevious,
		immediateNext,
	};
};
